import { useCallback, useEffect, useMemo } from 'react';
import { useTracking } from 'react-tracking';
import { Text, Box, Grid } from '@simplywallst/ui-core';
import CrossSolid from '@simplywallst/ui-core/icons/CrossSolid';
import TickSolid from '@simplywallst/ui-core/icons/TickSolid';

import {
  formatMonthlyPrice,
  getDiscountedPlanAmount,
} from '@/components/OurPlans/components/Plans/utils';
import Tooltip from '@/components/Tooltip';
import { useUserPlans } from '@/hooks/useUserPlans';
import { useUserAccess } from '@/hooks/useUserAccess';
import useUser, { CouponState } from '@/hooks/useUser';
import { PlanIllustration } from '../PlanIllustration';
import {
  AddedFeatures,
  BillingPeriodTextContainer,
  FeatureItem,
  MarketingText,
  PlanContainer,
  PlanContent,
  PlansContainer,
  StyledText,
} from './styled';
import PlanOffer from '../PlanOffer/PlanOffer';
import { PlanButton } from '../PlanButton/PlanButton';
import {
  BillingPeriod,
  DefaultButtonState,
  GrandfatherCoupons,
  usePlanButtonState,
  UserStatus,
  userStatusData,
} from '../../constants';
import type { ButtonState, Plan } from '../../interface';
import { useLocale } from '@/hooks/useLocale';
import { useTranslation } from '@/hooks/useTranslation';
import { PlanType } from '@simplywallst/services';

interface Props {
  darkBackground?: boolean;
  hideFreePlan?: boolean;
  userPlan: Plan;
  coupon: CouponState;
  onClose?: () => void;
  onAction?: () => void;
}

const getUserStatus = (
  hasSeenTrialEndModal: boolean,
  isUserLoggedIn: boolean,
  planStatus: string,
  userPlan: Plan
): UserStatus => {
  if (!isUserLoggedIn) return UserStatus.guest;
  if (hasSeenTrialEndModal !== true && userPlan.type === 'free')
    return UserStatus.trialEnded;
  if (userPlan.type === 'free') return UserStatus.free;
  if (planStatus === 'trialing') return UserStatus.trial;
  return userStatusData[userPlan.type][userPlan.interval]
    ? userStatusData[userPlan.type][userPlan.interval].status
    : UserStatus.guest;
};

export const PlansSelection = ({
  darkBackground = false,
  hideFreePlan,
  userPlan,
  coupon,
  onAction,
  onClose,
}: Props) => {
  const { data: userData } = useUser();
  const { data: userAccessData } = useUserAccess();
  const { status, plans } = useUserPlans();
  const billingPeriod = 'year';
  const { trackEvent } = useTracking({
    type: 'track',
  });
  const locale = useLocale();
  const { getButtonState } = usePlanButtonState();
  const getPlanBtnState = (
    plan: Plan,
    userPlan: Plan,
    billingPeriod: BillingPeriod
  ): ButtonState => {
    const planType = plan.type;
    const userStatus = getUserStatus(
      userData.hasSeenTrialEndModal,
      userData.loggedIn,
      userData.billing.status,
      userPlan
    );
    const planBtnState = getButtonState(
      userData.billing.canceled,
      userData.billing.canceledAt,
      userPlan.name
    );
    if (
      (planType === 'premium' || planType === 'unlimited') &&
      (userStatus === UserStatus.premiumMonthly ||
        userStatus === UserStatus.premiumYearly ||
        userStatus === UserStatus.unlimitedMonthly ||
        userStatus === UserStatus.unlimitedYearly)
    ) {
      return planBtnState[planType][userStatus][billingPeriod]
        ? planBtnState[planType][userStatus][billingPeriod]
        : DefaultButtonState;
    }
    return planBtnState[planType][userStatus]
      ? planBtnState[planType][userStatus]
      : DefaultButtonState;
  };

  //Temporarily show grandfather discount for unlimited plan only, until
  //backend supports multiple coupons per plan and user
  const isGrandfatherCoupon = GrandfatherCoupons.includes(
    userData.billing.coupon.id
  );

  const mappedPlans = useMemo(() => {
    if (status !== 'success') {
      return [];
    }
    return plans
      .filter((plan) => {
        const planValues = Object.values(plan)[0];
        if (planValues.type === 'advisor') return false;

        if (planValues.type === 'free' && !hideFreePlan) return true;
        if (
          planValues.type === 'free' &&
          !hideFreePlan &&
          planValues.interval === billingPeriod &&
          planValues.visible
        )
          return true;
        if (
          planValues.type !== 'free' &&
          planValues.interval === billingPeriod &&
          planValues.visible
        )
          return true;
        else return false;
      })
      .map((plan) => Object.values(plan)[0]);
  }, [plans, status, billingPeriod, hideFreePlan]);

  const getMonthlyPriceDiscounted = useCallback(
    (plan: Plan) => {
      const discountedPlanAmount = getDiscountedPlanAmount(
        plan.amount,
        coupon.percentOff
      );
      const planAmount =
        (isGrandfatherCoupon && plan.type === 'unlimited') ||
        !isGrandfatherCoupon
          ? discountedPlanAmount
          : plan.amount;
      return formatMonthlyPrice(
        planAmount,
        plan.interval,
        plan.currencyISO,
        locale
      );
    },
    [coupon.percentOff, isGrandfatherCoupon, locale]
  );

  useEffect(() => {
    if (!mappedPlans.length) {
      return;
    }

    const plans: Record<string, string>[] = mappedPlans.map((plan) => ({
      plan: plan.name,
      price: getMonthlyPriceDiscounted(plan),
      coupon: coupon.id,
    }));

    trackEvent({
      action: 'landed',
      plans: plans,
    });
  }, [coupon, getMonthlyPriceDiscounted, mappedPlans, trackEvent]);

  const t = useTranslation();

  const featureList = useMemo(() => {
    return [
      { code: 'globalMarkets', getText: () => t('plan.feature.globalMarkets') },
      {
        code: 'companyReportsPerMonth',
        getText: (count?: number) =>
          t('plan.feature.limitCompanyReports', { count }),
      },
      {
        code: 'companiesAnalysedInPortfolio',
        getText: () => 'Portfolio Stocks Analysed',
      },
      { code: 'watchlist', getText: () => 'Unlimited Watchlists' },
      {
        code: 'stockScreener',
        getText: (count?: number) =>
          t('plan.feature.limitStockScreener', { count }),
      },
      { code: 'exportToPdf', getText: () => t('plan.feature.export') },
    ] as const;
  }, [t]);

  function getPlanBlurb(plan: PlanType) {
    switch (plan) {
      case 'free':
        return t('plan.free.blurb');
      case 'premium':
        return t('plan.premium.blurb');
      case 'unlimited':
        return t('plan.unlimited.blurb');
      default:
        return '';
    }
  }

  return (
    <PlansContainer
      justifyContent="center"
      hideFreePlan={hideFreePlan || false}
    >
      {mappedPlans &&
        mappedPlans.map((plan, i) => {
          const monthlyPrice = formatMonthlyPrice(
            plan.amount,
            plan.interval,
            plan.currencyISO,
            locale
          );
          const monthlyPriceDiscounted = getMonthlyPriceDiscounted(plan);
          const { action, btnStyle, cta } = getPlanBtnState(
            plan,
            userPlan,
            billingPeriod
          );

          return (
            <PlanContainer
              col={12}
              colMd={hideFreePlan ? 6 : 4}
              key={`plan-${i}`}
            >
              <PlanIllustration planType={plan.type} />
              <PlanContent planType={plan.type}>
                <h2>{plan.name}</h2>
                <Text
                  typography="base"
                  color="spaceGrayRGBA"
                  opacity={0.7}
                  data-cy-id={`${plan.type}-price`}
                >
                  {plan.amount === 0 ? (
                    <>
                      <mark>{monthlyPrice}</mark>
                    </>
                  ) : (
                    <>
                      {plan.currencyISO} <mark>{monthlyPriceDiscounted}</mark>
                      /month
                    </>
                  )}
                </Text>
                {RUNTIME_ENV !== 'native' &&
                  ((isGrandfatherCoupon && plan.type === 'unlimited') ||
                    !isGrandfatherCoupon) && (
                    <PlanOffer
                      plan={plan}
                      couponId={coupon.id}
                      couponRedeemBy={coupon.redeemBy}
                    />
                  )}
                <BillingPeriodTextContainer>
                  <Text typography="base" color="spaceGray">
                    {plan.type !== 'free' && t('modal.upgrade.billedYearly')}
                  </Text>
                </BillingPeriodTextContainer>
                <MarketingText data-cy-id={`${plan.type}-marketing-text`}>
                  <Text typography="base" color="spaceGrayRGBA" opacity={0.7}>
                    {getPlanBlurb(plan.type)}
                  </Text>
                </MarketingText>
                {((RUNTIME_ENV === 'native' && action === 'continue') ||
                  RUNTIME_ENV !== 'native') && (
                  <PlanButton
                    btnStyle={btnStyle}
                    action={action}
                    plan={plan}
                    isLoggedIn={userData.loggedIn}
                    couponId={coupon.id}
                    onClick={onAction}
                    onComplete={onClose}
                  >
                    {cta}
                  </PlanButton>
                )}
                <ul>
                  {featureList.map((feature) => {
                    /**
                     * this entire block needs some love
                     */

                    const value = plan.features[feature.code];
                    const valueText = value === -1 ? 'Unlimited' : value;

                    let displayText =
                      typeof valueText === 'number'
                        ? feature.getText(valueText)
                        : feature.getText();

                    if (valueText === 'Unlimited') {
                      displayText = t('plan.feature.unlimitedCompanyReports');
                    }

                    const Icon = valueText === false ? CrossSolid : TickSolid;

                    if (plan.type === 'free') {
                      if (displayText.includes('Company Reports per Month')) {
                        const isUsingUserAccessData =
                          userAccessData.brokerReferrer &&
                          userAccessData.viewCompanyReports;
                        const defaultLimitPlan = plan.uacCompaniesAnalysis || 5;
                        const limitViewReportsPerMonth = isUsingUserAccessData
                          ? userAccessData.viewCompanyReports
                          : defaultLimitPlan;
                        displayText = t('plan.feature.limitCompanyReports', {
                          count: limitViewReportsPerMonth,
                        });
                      }

                      if (displayText.includes('Portfolio')) {
                        displayText = t('plan.feature.limitedPortfolio', {
                          portfolioCount: 1,
                          holdingsCount: 10,
                        });
                      }

                      if (feature.code === 'watchlist') {
                        displayText = t('plan.feature.limitedWatchlist', {
                          count: 1,
                        });
                      }
                      if (feature.code === 'stockScreener') {
                        displayText = t('plan.feature.freeLimitStockScreener');
                      }
                    }
                    if (plan.type === 'premium') {
                      if (feature.code === 'stockScreener') {
                        displayText = t('plan.feature.limitStockScreener', {
                          count: 3,
                        });
                      }

                      if (displayText.includes('Portfolio')) {
                        displayText = t('plan.feature.limitedPortfolio', {
                          portfolioCount: 3,
                          holdingsCount: 30,
                        });
                      }

                      if (feature.code === 'watchlist') {
                        displayText = t('plan.feature.limitedWatchlist', {
                          count: 3,
                        });
                      }
                    }
                    if (plan.type === 'unlimited') {
                      if (feature.code === 'companiesAnalysedInPortfolio') {
                        displayText = t('plan.feature.unlimitedHoldings', {
                          portfolioCount: 5,
                        });
                      }

                      if (feature.code === 'watchlist') {
                        displayText = t('plan.feature.limitedWatchlist', {
                          count: 5,
                        });
                      }
                      if (feature.code === 'stockScreener') {
                        displayText = t('plan.feature.limitStockScreener', {
                          count: 10,
                        });
                      }
                    }

                    return (
                      value !== undefined &&
                      valueText !== false && (
                        <FeatureItem key={feature.code} notIncluded={false}>
                          <div>
                            <div>
                              <Icon />
                            </div>
                            <Text
                              typography="base"
                              color="spaceGrayRGBA"
                              opacity={0.7}
                            >
                              {displayText}
                            </Text>
                          </div>
                        </FeatureItem>
                      )
                    );
                  })}
                </ul>
              </PlanContent>
              {plan.type === 'unlimited' && (
                <Box marginTop="x2">
                  <Tooltip
                    title={
                      <Box maxWidth="360px" padding="x1">
                        <Text
                          textAlign="center"
                          typography="xSmall"
                          color="spaceGray"
                        >
                          {t(
                            'plan.unlimited.tooltip.portfolioAndScreenerLimits'
                          )}
                        </Text>
                      </Box>
                    }
                    light
                    placement="top"
                    zIndex={99999}
                  >
                    <Grid placeContent="center">
                      <StyledText darkBackground={darkBackground}>
                        {t(
                          'plan.unlimited.footnote.portfolioAndScreenerLimits'
                        )}
                      </StyledText>
                    </Grid>
                  </Tooltip>
                </Box>
              )}
            </PlanContainer>
          );
        })}
      {mappedPlans.length > 0 && !hideFreePlan && RUNTIME_ENV !== 'native' && (
        <AddedFeatures darkBackground={darkBackground}>
          <FeatureItem notIncluded={false}>
            <TickSolid />
            {t('modal.upgrade.footer.taxDeductible')}
          </FeatureItem>
          <FeatureItem notIncluded={false}>
            <TickSolid />
            {t('modal.upgrade.footer.moneyBack')}
          </FeatureItem>
          <FeatureItem notIncluded={false}>
            <TickSolid />
            {t('modal.upgrade.footer.cancel')}
          </FeatureItem>
          <FeatureItem notIncluded={false}>
            <TickSolid />
            {t('modal.upgrade.footer.snp')}
          </FeatureItem>
        </AddedFeatures>
      )}
    </PlansContainer>
  );
};
